import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { AgeGroup } from "../mock-data";

// Validation schema (same as step7)
const editAgeGroupSchema = z.object({
  name: z
    .string()
    .min(1, "Le nom du groupe est requis")
    .max(30, "Nombre de caractères maximum : 30"),
  minAge: z
    .number()
    .min(0, "L'âge minimum doit être positif")
    .int("Entrer uniquement des nombres"),
  maxAge: z
    .number()
    .min(0, "L'âge maximum doit être positif")
    .int("Entrer uniquement des nombres"),
});

type EditAgeGroupFormValues = z.infer<typeof editAgeGroupSchema>;

interface EditAgeGroupSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: AgeGroup | null; // null for create mode
  existingGroups: AgeGroup[];
  onSave: (data: AgeGroup) => void;
}

export function EditAgeGroupSheet({
  open,
  onOpenChange,
  group,
  existingGroups,
  onSave,
}: EditAgeGroupSheetProps) {
  const [isLoading, setIsLoading] = useState(false);
  const isEditMode = group !== null;

  const form = useForm<EditAgeGroupFormValues>({
    resolver: zodResolver(editAgeGroupSchema),
    defaultValues: {
      name: group?.name || "",
      minAge: group?.minAge ?? undefined,
      maxAge: group?.maxAge ?? undefined,
    },
  });

  // Reset form when group changes
  if (open && group && form.getValues("name") !== group.name) {
    form.reset({
      name: group.name,
      minAge: group.minAge,
      maxAge: group.maxAge,
    });
  }

  if (open && !group && form.getValues("name") !== "") {
    form.reset({
      name: "",
      minAge: undefined,
      maxAge: undefined,
    });
  }

  // Validation function (same logic as step7)
  const validateAgeGroup = (data: EditAgeGroupFormValues): string | null => {
    // Check that min < max
    if (data.minAge >= data.maxAge) {
      return "L'âge minimum doit être inférieur à l'âge maximum";
    }

    // Check for duplicate name (excluding current group in edit mode)
    const otherGroups = existingGroups.filter((g) => !isEditMode || g.id !== group?.id);
    if (otherGroups.some((g) => g.name === data.name)) {
      return "Un groupe avec ce nom existe déjà";
    }

    // Check for age range overlap
    const hasOverlap = otherGroups.some(
      (g) =>
        (data.minAge >= g.minAge && data.minAge <= g.maxAge) ||
        (data.maxAge >= g.minAge && data.maxAge <= g.maxAge) ||
        (data.minAge <= g.minAge && data.maxAge >= g.maxAge)
    );

    if (hasOverlap) {
      return "Cette tranche d'âge chevauche un groupe déjà créé";
    }

    return null;
  };

  const onSubmit = async (data: EditAgeGroupFormValues) => {
    const error = validateAgeGroup(data);
    if (error) {
      toast.error("Erreur de validation", { description: error });
      return;
    }

    setIsLoading(true);

    try {
      const savedGroup: AgeGroup = {
        id: group?.id || crypto.randomUUID(),
        name: data.name,
        minAge: data.minAge,
        maxAge: data.maxAge,
      };

      onSave(savedGroup);
      onOpenChange(false);
      toast.success(isEditMode ? "Groupe modifié" : "Groupe ajouté");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="h-screen max-w-md! overflow-y-auto [&>button]:hidden">
        <SheetHeader className="sticky top-0 z-2 border-b bg-white shadow-xs">
          <SheetTitle>
            {isEditMode ? "Modifier le groupe" : "Ajouter un groupe d'âge"}
          </SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4 px-8">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nom du groupe <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="ex: Les petits" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="minAge"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Âge min. (inclus) <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="3"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          const parsed = parseInt(v);
                          field.onChange(v === "" || isNaN(parsed) ? undefined : parsed);
                        }}
                        min={0}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maxAge"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Âge max. (inclus) <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="6"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          const parsed = parseInt(v);
                          field.onChange(v === "" || isNaN(parsed) ? undefined : parsed);
                        }}
                        min={0}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <SheetFooter className="mt-8 px-8 gap-2">
              <SheetClose asChild>
                <Button type="button" variant="outline">
                  Annuler
                </Button>
              </SheetClose>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sauvegarde...
                  </>
                ) : isEditMode ? (
                  "Modifier"
                ) : (
                  "Ajouter"
                )}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
